import { useCallback } from 'react';
import { supabase } from './supabase';
import { getPublicUrl } from './storageHelpers';
import { useGameMetrics } from './useGameMetrics';
import { Game } from './useGame';

export const useGameList = () => {
  const gameMetrics = useGameMetrics();

  const listMyGames = useCallback(async (
    userId: string,
    page: number,
    pageSize: number = 12,
    includeGameMetrics: boolean = false,
  ): Promise<{ games: Game[]; hasMore: boolean }> => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    let query = supabase
      .from('games')
      .select(`
        *,
        profiles!games_user_id_fkey (user_tag)
      `, { count: 'exact' })
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .range(start, end);

    const { data: games, error: gamesError, count } = await query;

    if (gamesError) throw gamesError;
    if (!games) return { games: [], hasMore: false };

    // Extract user_tag from profiles and transform screenshot_url for each game
    games.forEach(game => {
      game.user_tag = '@' + game.profiles?.user_tag;
      if (game.screenshot_url) {
        // If the path is already a storage path, use it directly
        if (!game.screenshot_url.startsWith('data:') && !game.screenshot_url.includes('http')) {
          game.screenshot_url = getPublicUrl(supabase, game.screenshot_url);
        }
      }
      delete game.profiles;
    });
    
    // If includeGameMetrics is true, fetch game metrics for each game
    if (includeGameMetrics) {
      // Fetch metrics for all games in parallel
      const metricsPromises = games.map(async (game) => {
        try {
          const metrics = await gameMetrics.getGameMetrics(game.id);
          if (metrics) {
            // Add metrics data to the game object
            (game as any).gameMetrics = {
              plays: metrics.plays || 0,
              likes: metrics.likes || 0,
              remixes: metrics.remixes || 0,
            };
          } else {
            // Set default metrics if none exist
            (game as any).gameMetrics = {
              plays: 0,
              likes: 0,
              remixes: 0,
            };
          }
        } catch (error) {
          console.error(`Error fetching metrics for game ${game.id}:`, error);
          // Set default metrics on error
          (game as any).gameMetrics = {
            plays: 0,
            likes: 0,
            remixes: 0,
          };
        }
      });
      
      // Wait for all metrics to be fetched
      await Promise.all(metricsPromises);
    }

    return {
      games: games as Game[],
      hasMore: (count || 0) > end + 1
    };
  }, []);

  const listAllGames = useCallback(async (
    userId: string | null,
    page: number,
    pageSize: number = 12,
    includeGameMetrics: boolean = false,
  ): Promise<{ games: Game[]; hasMore: boolean }> => {
    // Call the stored procedure to get interleaved games
    const { data, error, count } = await supabase
      .rpc('get_interleaved_games_list', {
        p_page: page,
        p_page_size: pageSize
      });

    if (error) throw error;
    
    // Process the games from the stored procedure
    const games = data || [];
    
    // Extract the total count from the first game (all games have the same total_count)
    const totalCount = games.length > 0 ? games[0].total_count : 0;
    
    // Process each game to format user_tag and screenshot_url
    const processedGames = games.map(game => {
      // Format user_tag with @ prefix
      game.user_tag = '@' + game.user_tag;
      
      // Process screenshot URL if needed
      if (game.screenshot_url) {
        if (!game.screenshot_url.startsWith('data:') && !game.screenshot_url.includes('http')) {
          game.screenshot_url = getPublicUrl(supabase, game.screenshot_url);
        }
      }
      
      // Add game metrics directly to the game object if they exist in the result
      (game as any).gameMetrics = {
        plays: game.plays || 0,
        likes: game.likes || 0,
        remixes: game.remixes || 0,
      };
      
      // Remove the flat metrics properties as they're now in the gameMetrics object
      delete game.plays;
      delete game.likes;
      delete game.remixes;
      delete game.total_count; // Remove the total_count property as it's not part of the Game type
      
      return game;
    });
    
    // Determine if there are more games available using the total count from the SQL function
    // Compare the current page's end position with the total count of distinct games
    const hasMore = totalCount > (page * pageSize);
    // console.log('hasMore:', hasMore, totalCount-(page * pageSize));
    
    const result = {
      games: processedGames as Game[],
      hasMore
    };
    // console.log('Result:', result);
    return result;
  }, [gameMetrics]);

  return {
    listMyGames,
    listAllGames,
  };
};
